// Cookie utility functions for theme persistence
export interface CookieOptions {
  expires?: Date | number; // Date object or days from now
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Set a cookie with the given name, value, and options
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  if (typeof document === 'undefined') {
    console.warn('setCookie called in non-browser environment');
    return;
  }

  try {
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    // Handle expires option
    if (options.expires) {
      let expiresDate: Date;
      if (typeof options.expires === 'number') {
        // If it's a number, treat it as days from now
        expiresDate = new Date();
        expiresDate.setTime(expiresDate.getTime() + (options.expires * 24 * 60 * 60 * 1000));
      } else {
        expiresDate = options.expires;
      }
      cookieString += `; expires=${expiresDate.toUTCString()}`;
    }

    // Add other options
    if (options.path) {
      cookieString += `; path=${options.path}`;
    }
    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }
    if (options.secure) {
      cookieString += `; secure`;
    }
    if (options.sameSite) {
      cookieString += `; sameSite=${options.sameSite}`;
    }

    document.cookie = cookieString;
  } catch (error) {
    console.warn('Failed to set cookie:', name, error);
  }
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    console.warn('getCookie called in non-browser environment');
    return null;
  }

  try {
    const encodedName = encodeURIComponent(name);
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(encodedName + '=')) {
        return decodeURIComponent(cookie.substring(encodedName.length + 1));
      }
    }

    return null;
  } catch (error) {
    console.warn('Failed to get cookie:', name, error);
    return null;
  }
}

/**
 * Remove a cookie by setting it to expire in the past
 */
export function removeCookie(name: string, options: Omit<CookieOptions, 'expires'> = {}): void {
  if (typeof document === 'undefined') {
    console.warn('removeCookie called in non-browser environment');
    return;
  }

  try {
    setCookie(name, '', {
      ...options,
      expires: new Date(0), // Set to epoch time to delete
    });
  } catch (error) {
    console.warn('Failed to remove cookie:', name, error);
  }
}

/**
 * Check if cookies are available in the current environment
 */
export function areCookiesAvailable(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  try {
    // Try to set and read a test cookie
    const testName = '__test_cookie__';
    const testValue = 'test';
    setCookie(testName, testValue);
    const retrieved = getCookie(testName);
    removeCookie(testName);
    return retrieved === testValue;
  } catch (error) {
    return false;
  }
}

/**
 * Get all cookies as an object
 */
export function getAllCookies(): Record<string, string> {
  if (typeof document === 'undefined') {
    console.warn('getAllCookies called in non-browser environment');
    return {};
  }

  try {
    const cookies: Record<string, string> = {};
    const cookieStrings = document.cookie.split(';');

    for (let cookie of cookieStrings) {
      cookie = cookie.trim();
      const [name, value] = cookie.split('=');
      if (name && value) {
        cookies[decodeURIComponent(name)] = decodeURIComponent(value);
      }
    }

    return cookies;
  } catch (error) {
    console.warn('Failed to get all cookies:', error);
    return {};
  }
} 